import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Typography,
  Button,
  Space,
  message,
  Popconfirm,
  Modal,
  Form,
  Input,
  Tag,
  Tooltip,
  Spin,
  Skeleton,
  Alert,
  Steps,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  VideoCameraOutlined,
  KeyOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  CloudServerOutlined,
  AppstoreOutlined,
  QuestionCircleOutlined,
  UnorderedListOutlined,
  CameraOutlined,
  SaveOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import {
  getEzvizVideoTokens,
  createEzvizVideoToken,
  deleteEzvizVideoToken,
  updateEzvizVideoToken,
  EzvizVideoToken,
} from '../../services/api';
import './styles.css';

const { Title, Text } = Typography;
const { Search } = Input;

// 获取 Token 过期状态
const getTokenExpiryStatus = (timestamp: number) => {
  const now = Date.now();
  const diff = timestamp - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days <= 0) return { text: '已过期', color: 'red' as const };
  if (days <= 3) return { text: `${days}天后过期`, color: 'orange' as const };
  if (days <= 7) return { text: `${days}天后过期`, color: 'gold' as const };
  return { text: `${days}天后过期`, color: 'green' as const };
};

const EzvizVideoManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [videoTokens, setVideoTokens] = useState<EzvizVideoToken[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingToken, setEditingToken] = useState<EzvizVideoToken | null>(null);
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState<Set<string>>(new Set());

  const fetchVideoTokens = async (
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
    search?: string,
  ) => {
    try {
      setLoading(true);
      const { data } = await getEzvizVideoTokens(page, pageSize, search ?? searchValue);
      if (data.code === 0 && data.data) {
        setVideoTokens(data.data.items || []);
        setPagination({
          current: page,
          pageSize,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
        });
      } else {
        setVideoTokens([]);
        message.error(data.msg || '获取萤石云视频账户失败');
      }
    } catch (error) {
      console.error('Error fetching video tokens:', error);
      setVideoTokens([]);
      message.error('获取萤石云视频账户失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideoTokens(1, 10, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchVideoTokens(1, pagination.pageSize, searchValue);
  };

  const handleTableChange = (pag: any) => {
    fetchVideoTokens(pag.current, pag.pageSize, searchValue);
  };

  const handleAddToken = () => {
    setIsEditing(false);
    setEditingToken(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: EzvizVideoToken) => {
    setIsEditing(true);
    setEditingToken(record);
    form.setFieldsValue({
      appKey: record.appKey,
      appSecret: record.appSecret,
      remark: record.remark,
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isEditing && editingToken) {
        const { data } = await updateEzvizVideoToken(editingToken.uid, {
          appKey: values.appKey,
          appSecret: values.appSecret,
          remark: values.remark,
        });
        if (data.code === 0) {
          message.success('更新萤石云视频账户成功');
          setIsModalVisible(false);
          form.resetFields();
          fetchVideoTokens();
        } else {
          message.error(data.msg || '更新萤石云视频账户失败');
        }
      } else {
        const { data } = await createEzvizVideoToken({
          appKey: values.appKey,
          appSecret: values.appSecret,
          remark: values.remark,
        });
        if (data.code === 0) {
          message.success('添加萤石云视频账户成功');
          setIsModalVisible(false);
          form.resetFields();
          fetchVideoTokens();
        } else {
          message.error(data.msg || '添加萤石云视频账户失败');
        }
      }
    } catch (error) {
      console.error('Error submitting video token:', error);
      message.error(isEditing ? '更新萤石云视频账户失败' : '添加萤石云视频账户失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (uid: string) => {
    setDeletingIds((prev) => [...prev, uid]);
    try {
      const { data } = await deleteEzvizVideoToken(uid);
      if (data.code === 0) {
        message.success('删除萤石云视频账户成功');
        fetchVideoTokens();
      } else {
        message.error(data.msg || '删除萤石云视频账户失败');
      }
    } catch (error) {
      console.error('Error deleting video token:', error);
      message.error('删除萤石云视频账户失败');
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== uid));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const toggleSecretVisibility = (uid: string) => {
    setVisibleSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const totalTokens = pagination.total;
  const expiredTokens = videoTokens.filter((t) => t.tokenExpire < Date.now()).length;
  const validTokens = videoTokens.filter((t) => t.tokenExpire >= Date.now()).length;

  const columns = [
    {
      title: '账户信息',
      key: 'account',
      render: (_: any, record: EzvizVideoToken) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.remark || '未备注'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>UID: {record.uid}</div>
        </div>
      ),
    },
    {
      title: 'AppKey',
      dataIndex: 'appKey',
      key: 'appKey',
      render: (text: string) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }}>{text}</Text>
          <a onClick={() => copyToClipboard(text)}><CopyOutlined /></a>
        </Space>
      ),
    },
    {
      title: 'AppSecret',
      dataIndex: 'appSecret',
      key: 'appSecret',
      render: (text: string, record: EzvizVideoToken) => {
        const isVisible = visibleSecrets.has(record.uid);
        const displayText = isVisible
          ? text
          : `${text.substring(0, 6)}${'•'.repeat(6)}${text.substring(text.length - 4)}`;
        return (
          <Space size={4}>
            <Text code style={{ fontSize: 12 }}>{displayText}</Text>
            <a onClick={() => toggleSecretVisibility(record.uid)}>
              {isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </a>
            <a onClick={() => copyToClipboard(text)}><CopyOutlined /></a>
          </Space>
        );
      },
    },
    {
      title: 'Token 状态',
      dataIndex: 'tokenExpire',
      key: 'tokenExpire',
      render: (timestamp: number) => {
        const expiry = getTokenExpiryStatus(timestamp);
        return (
          <div>
            <Tag color={expiry.color}>
              {timestamp < Date.now() ? '已过期' : '有效'}
            </Tag>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
              {new Date(timestamp).toLocaleDateString('zh-CN')}
            </div>
          </div>
        );
      },
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      render: (text: string) => <Tag color="blue">{text || '-'}</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: EzvizVideoToken) => (
        <Space size="middle">
          <a onClick={() => handleEdit(record)}>编辑</a>
          <Popconfirm
            title="确认删除"
            description="确定要删除此萤石云视频账户吗？"
            onConfirm={() => handleDelete(record.uid)}
            okText="确认"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <a style={{ color: '#ff4d4f' }}>删除</a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="ezviz-container">
      <Card className="ezviz-card">
        {/* 页面头部 */}
        <div className="ezviz-header">
          <Title level={2}>萤石云视频账户管理</Title>
          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddToken}
            >
              添加账户
            </Button>
            <Button
              icon={<QuestionCircleOutlined />}
              onClick={() => setShowGuide(true)}
            >
              使用指南
            </Button>
          </Space>
        </div>

        {/* 统计卡片 */}
        <div className="ezviz-stats-section">
          <div className="ezviz-stats-card">
            <div className="ezviz-stat-item">
              <div className="ezviz-stat-icon-wrapper">
                <VideoCameraOutlined className="ezviz-stat-icon" />
              </div>
              <div className="ezviz-stat-content">
                <div className="ezviz-stat-value">{totalTokens}</div>
                <div className="ezviz-stat-title">总账户数</div>
              </div>
            </div>
            <div className="ezviz-stat-divider" />
            <div className="ezviz-stat-item">
              <div className="ezviz-stat-icon-wrapper">
                <CheckCircleOutlined className="ezviz-stat-icon" />
              </div>
              <div className="ezviz-stat-content">
                <div className="ezviz-stat-value">{validTokens}</div>
                <div className="ezviz-stat-title">有效账户</div>
              </div>
            </div>
            <div className="ezviz-stat-divider" />
            <div className="ezviz-stat-item">
              <div className="ezviz-stat-icon-wrapper">
                <ExclamationCircleOutlined className="ezviz-stat-icon" />
              </div>
              <div className="ezviz-stat-content">
                <div className="ezviz-stat-value">{expiredTokens}</div>
                <div className="ezviz-stat-title">已过期</div>
              </div>
            </div>
          </div>
        </div>

        {/* 搜索栏 */}
        <div style={{ marginBottom: 16 }}>
          <Search
            placeholder="搜索 AppKey 或备注"
            allowClear
            enterButton={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            style={{ maxWidth: 360 }}
          />
        </div>

        {/* 表格 */}
        <Spin spinning={loading}>
          {loading && videoTokens.length === 0 ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Table
              dataSource={videoTokens}
              columns={columns}
              rowKey="uid"
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                simple: window.innerWidth <= 576,
              }}
              size="small"
            />
          )}
        </Spin>

        {/* 底部信息 */}
        <div className="ezviz-platform-footer">
          <Text type="secondary">智趣智慧工地数据管理平台</Text>
          <br />
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            辽ICP备12014462号-7
          </a>
        </div>
      </Card>

      {/* 使用指南弹窗 */}
      <Modal
        title="使用指南"
        open={showGuide}
        onCancel={() => setShowGuide(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowGuide(false)}>
            我知道了
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: '8px 0' }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 20, borderRadius: 8 }}
            message={<Text strong>页面功能</Text>}
            description={
              <Text type="secondary">
                本页面管理萤石云 API 凭证。配置后，在「设备管理」表单中系统自动关联账户，实现设备接入与视频流获取。
              </Text>
            }
          />

          <Steps
            direction="vertical"
            size="small"
            current={-1}
            items={[
              {
                title: <Text strong>在萤石云开放平台创建应用</Text>,
                description: (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    登录 <Text code style={{ fontSize: 12 }}>open.ys7.com</Text>，
                    在「控制台 → 应用管理」创建应用，获取 AppKey 和 AppSecret。
                  </Text>
                ),
                icon: <CloudServerOutlined style={{ color: '#1677ff' }} />,
              },
              {
                title: <Text strong>在本页面添加账户</Text>,
                description: (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    点击「添加账户」，填入 AppKey、AppSecret 和备注名称，系统自动管理 Token。
                  </Text>
                ),
                icon: <SaveOutlined style={{ color: '#52c41a' }} />,
              },
              {
                title: <Text strong>设备表单自动生效</Text>,
                description: (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    在「设备管理」新增/编辑设备时，系统自动加载萤石云账户、设备列表和通道。
                  </Text>
                ),
                icon: <AppstoreOutlined style={{ color: '#722ed1' }} />,
              },
              {
                title: <Text strong>选择设备与通道</Text>,
                description: (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    依次选择：账户 → 设备 → 通道，勾选后提交即可自动保存配置。
                  </Text>
                ),
                icon: <CameraOutlined style={{ color: '#fa8c16' }} />,
              },
            ]}
          />

          <Divider style={{ margin: '12px 0' }} />

          {/* 数据流向 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0,
            padding: '8px 0',
            flexWrap: 'wrap',
          }}>
            {[
              { icon: <SafetyCertificateOutlined />, title: '本页面', desc: 'API 凭证', color: '#1677ff' },
              { icon: <AppstoreOutlined />, title: '设备表单', desc: '自动加载', color: '#722ed1' },
              { icon: <UnorderedListOutlined />, title: '设备列表', desc: '萤石设备', color: '#13c2c2' },
              { icon: <CameraOutlined />, title: '通道选择', desc: '勾选摄像头', color: '#fa8c16' },
            ].map((item, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <ArrowRightOutlined style={{ fontSize: 16, color: '#d9d9d9', margin: '0 6px' }} />}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: `${item.color}08`,
                  borderRadius: 8,
                  border: `1px solid ${item.color}20`,
                  minWidth: 80,
                  textAlign: 'center',
                }}>
                  {React.cloneElement(item.icon as React.ReactElement, { style: { fontSize: 16, color: item.color, marginBottom: 4 } })}
                  <Text strong style={{ fontSize: 11 }}>{item.title}</Text>
                  <Text type="secondary" style={{ fontSize: 10 }}>{item.desc}</Text>
                </div>
              </React.Fragment>
            ))}
          </div>

          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12, borderRadius: 8 }}
            message={
              <Text type="secondary" style={{ fontSize: 12 }}>
                <Text strong>注意：</Text>Token 会自动过期，请关注过期提醒，及时更新避免影响视频接入。
              </Text>
            }
          />
        </div>
      </Modal>

      {/* 添加/编辑 Token 弹窗 */}
      <Modal
        title={isEditing ? '编辑萤石云视频账户' : '添加萤石云视频账户'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        maskClosable={false}
        width={520}
        okText={isEditing ? '保存修改' : '确认添加'}
        cancelText="取消"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="请确保输入的 AppKey 和 AppSecret 与萤石云开放平台中创建的应用一致。"
        />
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="appKey"
            label="AppKey"
            rules={[{ required: true, message: '请输入 AppKey' }]}
            extra="萤石云开放平台应用的唯一标识"
          >
            <Input placeholder="请输入 AppKey" />
          </Form.Item>

          <Form.Item
            name="appSecret"
            label="AppSecret"
            rules={[{ required: true, message: '请输入 AppSecret' }]}
            extra="萤石云开放平台应用的密钥，请妥善保管"
          >
            <Input.Password placeholder="请输入 AppSecret" />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注名称"
            rules={[{ required: true, message: '请输入备注' }]}
            extra="为该账户设置一个易识别的名称"
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="例如：生产环境账户、测试环境账户" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EzvizVideoManagement;
